import React from "react";
import ewtStore from "../EWTStore";
import { theme, roundHandicapForScoring } from "../theme";

const LABEL_COL_WIDTH = 120;
const HOLE_COL_WIDTH = 40;
const TOTAL_COL_WIDTH = 58;
const SCROLL_WIDTH = 18 * HOLE_COL_WIDTH + 2 * TOTAL_COL_WIDTH;
const HEADER_ROW_HEIGHT = 32;
const DATA_ROW_HEIGHT = 30;

const holeCellStyle = {
  width: HOLE_COL_WIDTH,
  minWidth: HOLE_COL_WIDTH,
  maxWidth: HOLE_COL_WIDTH,
};

const totalCellStyle = {
  width: TOTAL_COL_WIDTH,
  minWidth: TOTAL_COL_WIDTH,
  maxWidth: TOTAL_COL_WIDTH,
};

const labelCellStyle = {
  width: LABEL_COL_WIDTH,
  minWidth: LABEL_COL_WIDTH,
  maxWidth: LABEL_COL_WIDTH,
};

const headerRowStyle = { height: HEADER_ROW_HEIGHT };
const dataRowStyle = { height: DATA_ROW_HEIGHT };

const computeHoleRanks = (rounds, computedScores, showGross) => {
  const vsPar = {};
  for (let h = 1; h <= 18; h++) {
    let total = 0;
    let count = 0;
    rounds.forEach((round) => {
      const holeData = computedScores[round.id]?.holes?.[h];
      if (holeData && holeData.score != null && holeData.par != null) {
        if (showGross) {
          total += holeData.score - holeData.par;
        } else if (holeData.strokes != null) {
          total += holeData.strokes;
        }
        count++;
      }
    });
    if (count > 0) vsPar[h] = total;
  }

  const sorted = Object.keys(vsPar)
    .map((h) => ({ hole: parseInt(h, 10), vsPar: vsPar[h] }))
    .sort((a, b) => a.vsPar - b.vsPar);

  const ranks = {};
  let rank = 1;
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i].vsPar > sorted[i - 1].vsPar) {
      rank = i + 1;
    }
    ranks[sorted[i].hole] = rank;
  }

  return ranks;
};

const computeHoleAverages = (rounds, computedScores, showGross) => {
  const averages = {};
  for (let h = 1; h <= 18; h++) {
    let total = 0;
    let count = 0;
    rounds.forEach((round) => {
      const holeData = computedScores[round.id]?.holes?.[h];
      if (!holeData || holeData.score == null || holeData.score === "") return;
      if (showGross) {
        total += holeData.score;
      } else if (holeData.strokes != null && holeData.par != null) {
        total += holeData.par + holeData.strokes;
      } else {
        return;
      }
      count++;
    });
    if (count > 0) {
      averages[h] = Math.round((total / count) * 10) / 10;
    }
  }
  return averages;
};

const computeAverageTotal = (averages) => {
  let total = 0;
  let hasAny = false;
  for (let h = 1; h <= 18; h++) {
    if (averages[h] != null) {
      total += averages[h];
      hasAny = true;
    }
  }
  if (!hasAny) return null;
  return Math.round(total * 10) / 10;
};

const computeHolePars = (rounds, coursesById) => {
  const pars = {};
  let totalPar = 0;
  for (let h = 1; h <= 18; h++) {
    let holePar = null;
    for (const round of rounds) {
      const course = coursesById && coursesById[round.courseID];
      const tee = course?.holes?.[h]?.tees?.[round.tees];
      if (tee && tee.par != null) {
        holePar = tee.par;
        break;
      }
    }
    if (holePar != null) {
      pars[h] = holePar;
      totalPar += holePar;
    }
  }
  return { pars, totalPar };
};

const roundHasScores = (round, computedScores) => {
  const rowScores = computedScores[round.id];
  if (!rowScores || !rowScores.holes) return false;
  return Object.values(rowScores.holes).some(
    (holeData) => holeData && holeData.score != null && holeData.score !== ""
  );
};

const getHoleDisplay = (holeData, showGross) => {
  if (!holeData || holeData.score == null || holeData.score === "") {
    return { display: "", isBirdie: false, isPar: false };
  }
  if (showGross) {
    return {
      display: holeData.score,
      isBirdie: holeData.score < holeData.par,
      isPar: holeData.score === holeData.par,
    };
  }
  const netScore = holeData.par + holeData.strokes;
  return {
    display: netScore,
    isBirdie: holeData.strokes < 0,
    isPar: holeData.strokes === 0,
  };
};

const getNetTotalDisplay = (rowScores) => {
  if (rowScores.net === "" || rowScores.net == null) return "";
  if (rowScores.handicap != null && rowScores.handicap !== "") {
    return `${rowScores.net} (${roundHandicapForScoring(rowScores.handicap)})`;
  }
  return rowScores.net;
};

const ScrollableHeader = ({ averages, pars, totalPar, showGross }) => {
  const avgTotal = computeAverageTotal(averages);
  const avgTotalDisplay =
    avgTotal != null ? avgTotal.toFixed(1) : "";

  return (
  <div style={{ width: SCROLL_WIDTH }}>
    <div className="flexRow playerCardHistoryHeaderDataRow" style={headerRowStyle}>
      {Array.from({ length: 18 }, (_, i) => (
        <div
          key={i + 1}
          className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--hole"
          style={holeCellStyle}
        >
          {i + 1}
        </div>
      ))}
      <div
        className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--total"
        style={totalCellStyle}
      >
        Gr
      </div>
      <div
        className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--total"
        style={totalCellStyle}
      >
        Net
      </div>
    </div>
    <div className="flexRow playerCardHistoryHeaderDataRow" style={headerRowStyle}>
      {Array.from({ length: 18 }, (_, i) => {
        const hole = i + 1;
        return (
          <div
            key={`par-${hole}`}
            className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--hole"
            style={holeCellStyle}
          >
            {pars[hole] != null ? pars[hole] : ""}
          </div>
        );
      })}
      <div
        className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--total"
        style={totalCellStyle}
      >
        {totalPar || ""}
      </div>
      <div
        className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--total"
        style={totalCellStyle}
      />
    </div>
    <div className="flexRow playerCardHistoryHeaderDataRow" style={headerRowStyle}>
      {Array.from({ length: 18 }, (_, i) => {
        const hole = i + 1;
        return (
          <div
            key={`avg-${hole}`}
            className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--hole"
            style={holeCellStyle}
          >
            {averages[hole] != null ? averages[hole].toFixed(1) : ""}
          </div>
        );
      })}
      <div
        className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--total"
        style={totalCellStyle}
      >
        {showGross ? avgTotalDisplay : ""}
      </div>
      <div
        className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--total"
        style={totalCellStyle}
      >
        {!showGross ? avgTotalDisplay : ""}
      </div>
    </div>
  </div>
  );
};

const RankRow = ({ ranks }) => (
  <div className="flexRow playerCardHistoryDataRow" style={headerRowStyle}>
    {Array.from({ length: 18 }, (_, i) => {
      const hole = i + 1;
      return (
        <div
          key={`rank-${hole}`}
          className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--hole"
          style={holeCellStyle}
        >
          {ranks[hole] || ""}
        </div>
      );
    })}
    <div
      className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--total"
      style={totalCellStyle}
    />
    <div
      className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--total"
      style={totalCellStyle}
    />
  </div>
);

export const PlayerCardHistoryTable = ({
  rounds,
  computedScores,
  showGross,
  coursesById,
}) => {
  const headerScrollRef = React.useRef(null);
  const bodyScrollRef = React.useRef(null);
  const labelScrollRef = React.useRef(null);
  const syncingRef = React.useRef(false);

  const scoredRounds = (rounds || []).filter((round) =>
    roundHasScores(round, computedScores)
  );

  const ranks = computeHoleRanks(scoredRounds, computedScores, showGross);
  const averages = computeHoleAverages(scoredRounds, computedScores, showGross);
  const { pars, totalPar } = computeHolePars(scoredRounds, coursesById);

  const onBodyScroll = React.useCallback(() => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    const body = bodyScrollRef.current;
    if (body) {
      if (headerScrollRef.current) {
        headerScrollRef.current.scrollLeft = body.scrollLeft;
      }
      if (labelScrollRef.current) {
        labelScrollRef.current.scrollTop = body.scrollTop;
      }
    }
    syncingRef.current = false;
  }, []);

  const onHeaderScroll = React.useCallback(() => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    const header = headerScrollRef.current;
    if (header && bodyScrollRef.current) {
      bodyScrollRef.current.scrollLeft = header.scrollLeft;
    }
    syncingRef.current = false;
  }, []);

  if (scoredRounds.length === 0) {
    return (
      <div style={{ padding: 10, fontSize: 20 }}>No rounds to display.</div>
    );
  }

  return (
    <div className="playerCardHistoryLayout">
      <div className="playerCardHistoryTop">
        <div className="playerCardHistoryLabelHeader">
          <div
            className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--label"
            style={{ ...labelCellStyle, ...headerRowStyle }}
          >
            Hole
          </div>
          <div
            className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--label"
            style={{ ...labelCellStyle, ...headerRowStyle }}
          >
            Par
          </div>
          <div
            className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--label"
            style={{ ...labelCellStyle, ...headerRowStyle }}
          >
            Avg
          </div>
        </div>
        <div
          className="playerCardHistoryHeaderHScroll"
          ref={headerScrollRef}
          onScroll={onHeaderScroll}
        >
          <ScrollableHeader
            averages={averages}
            pars={pars}
            totalPar={totalPar}
            showGross={showGross}
          />
        </div>
      </div>
      <div className="playerCardHistoryMain">
        <div className="playerCardHistoryLabelBody" ref={labelScrollRef}>
          {scoredRounds.map((round) => {
            const tournament = round.tournament;
            return (
              <div
                key={round.id}
                className="playerCardHistoryGridCell playerCardHistoryGridCell--body playerCardHistoryGridCell--label"
                style={{ ...labelCellStyle, ...dataRowStyle }}
              >
                {ewtStore.formatRoundLabel(tournament, round)}
              </div>
            );
          })}
          <div
            className="playerCardHistoryGridCell playerCardHistoryGridCell--header playerCardHistoryGridCell--label"
            style={{ ...labelCellStyle, ...headerRowStyle }}
          >
            Rank
          </div>
        </div>
        <div
          className="playerCardHistoryBodyScroll"
          ref={bodyScrollRef}
          onScroll={onBodyScroll}
        >
          <div style={{ width: SCROLL_WIDTH }}>
            {scoredRounds.map((round) => {
              const rowScores = computedScores[round.id] || { holes: {} };
              return (
                <div
                  key={round.id}
                  className="flexRow playerCardHistoryDataRow"
                  style={dataRowStyle}
                >
                  {Array.from({ length: 18 }, (_, i) => {
                    const hole = i + 1;
                    const holeData = rowScores.holes[hole];
                    const { display, isBirdie, isPar } = getHoleDisplay(
                      holeData,
                      showGross
                    );
                    return (
                      <div
                        key={hole}
                        className="playerCardHistoryGridCell playerCardHistoryGridCell--body playerCardHistoryGridCell--hole"
                        style={{
                          ...holeCellStyle,
                          color: isBirdie ? "red" : theme.navy,
                          fontWeight: isBirdie || isPar ? "bold" : "normal",
                        }}
                      >
                        {display}
                      </div>
                    );
                  })}
                  <div
                    className="playerCardHistoryGridCell playerCardHistoryGridCell--body playerCardHistoryGridCell--total"
                    style={totalCellStyle}
                  >
                    {rowScores.gross}
                  </div>
                  <div
                    className="playerCardHistoryGridCell playerCardHistoryGridCell--body playerCardHistoryGridCell--total"
                    style={totalCellStyle}
                  >
                    {getNetTotalDisplay(rowScores)}
                  </div>
                </div>
              );
            })}
            <RankRow ranks={ranks} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerCardHistoryTable;
